/**
 * @fileoverview `<FaqList>` — liste de questions fréquentes.
 *
 * Fournit un patron accessible pour les pages d'aide citoyennes et backoffice.
 * Les réponses restent du contenu React : la page décide du texte, des liens
 * et des composants métier, tandis que le design system porte l'espacement,
 * l'ouverture native et l'icône de révélation.
 */

import type { ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../lib/utils.ts';

export interface FaqItem {
  /** Question affichée dans le résumé cliquable. */
  question: ReactNode;
  /** Réponse affichée quand l'item est ouvert. */
  answer: ReactNode;
  /** Ouvre l'item au rendu initial. */
  defaultOpen?: boolean;
}

export interface FaqListProps {
  /** Liste ordonnée des questions fréquentes. */
  items: readonly FaqItem[];
  className?: string;
}

/**
 * Liste FAQ basée sur `<details>/<summary>`.
 *
 * @param props - Items FAQ et classe CSS optionnelle.
 * @returns Liste de disclosures accessibles sans état React.
 *
 * @example
 * ```tsx
 * <FaqList
 *   items={[
 *     {
 *       question: 'Comment payer une contravention ?',
 *       answer: 'Recherchez votre plaque puis suivez le parcours de paiement.',
 *     },
 *   ]}
 * />
 * ```
 */
export function FaqList({ items, className }: FaqListProps): React.JSX.Element {
  return (
    <div className={cn('grid gap-3', className)}>
      {items.map((item, index) => (
        <details
          key={index}
          open={item.defaultOpen}
          className="border-border bg-card group rounded-lg border"
        >
          <summary className="text-quipux-navy-950 hover:bg-quipux-navy-50 focus-visible:ring-ring flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-3 text-sm font-semibold outline-none transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 [&::-webkit-details-marker]:hidden">
            <span>{item.question}</span>
            <ChevronDown className="text-muted-foreground size-4 shrink-0 transition-transform group-open:rotate-180" />
          </summary>
          <div className="border-border text-muted-foreground border-t px-4 py-3 text-sm leading-6">
            {item.answer}
          </div>
        </details>
      ))}
    </div>
  );
}
